import { getApiUrl } from '@/config/api'

export interface AuthContextResponse {
  managed_by_lti: boolean
  managed_by_sso: boolean
  org_has_sso: boolean
  /**
   * Org policy (ENG-3270): this organization forbids EnGen password
   * credentials. Unlike managed_by_sso this does not depend on the user's own
   * sign-in history, so it also covers members who have never gone through the
   * IdP. Trust this, not org_has_sso, when deciding to show the password card.
   */
  password_auth_disabled: boolean
  last_sign_in_method: 'lti' | 'saml' | 'oauth' | 'password' | null
  organization_name: string | null
}

export interface UpdatePasswordRequest {
  new_password: string
  current_password?: string
}

// Surfaces the backend's structured error so the caller can render
// localized messages (e.g. distinguish "wrong current password" from
// "password was found in a breach database").
export class UpdatePasswordError extends Error {
  readonly status: number
  readonly clerkCode: string | null

  constructor(message: string, status: number, clerkCode: string | null = null) {
    super(message)
    this.name = 'UpdatePasswordError'
    this.status = status
    this.clerkCode = clerkCode
  }
}

const parseUpdatePasswordError = async (response: Response): Promise<UpdatePasswordError> => {
  try {
    const body = await response.json()
    const detail = body?.detail
    if (detail && typeof detail === 'object') {
      return new UpdatePasswordError(
        typeof detail.message === 'string' ? detail.message : 'Password update failed.',
        response.status,
        typeof detail.clerk_code === 'string' ? detail.clerk_code : null
      )
    }
    if (typeof detail === 'string') {
      return new UpdatePasswordError(detail, response.status)
    }
  } catch {
    // Fall through to the generic error.
  }
  return new UpdatePasswordError('Password update failed.', response.status)
}

export class StampTimeZoneError extends Error {
  readonly status: number

  constructor(status: number) {
    super(`Time zone stamp failed with status ${status}`)
    this.name = 'StampTimeZoneError'
    this.status = status
  }
}

export const meService = {
  authContextUrl: () => getApiUrl('/me/auth-context'),
  updatePasswordUrl: () => getApiUrl('/me/password'),
  timeZoneUrl: () => getApiUrl('/me/time-zone'),
  preferredLocaleUrl: () => getApiUrl('/me/preferred-locale'),
  async setPreferredLocale(token: string, preferredLocale: string): Promise<void> {
    const response = await fetch(meService.preferredLocaleUrl(), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ preferred_locale: preferredLocale }),
    })
    if (!response.ok) {
      throw new Error(`Preferred locale update failed with status ${response.status}`)
    }
  },
  async stampTimeZone(token: string, timeZone: string): Promise<void> {
    const response = await fetch(meService.timeZoneUrl(), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ time_zone: timeZone }),
    })
    if (!response.ok) {
      throw new StampTimeZoneError(response.status)
    }
  },
  async updatePassword(token: string, payload: UpdatePasswordRequest): Promise<void> {
    const response = await fetch(meService.updatePasswordUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    })
    if (!response.ok) {
      throw await parseUpdatePasswordError(response)
    }
  },
}
